import { useCallback } from 'react';
import type { RssMaxItemsPerEpisode, RssMaxAge, RssProcessingDelay } from '@/__generated__/TopicFormCreateMutation.graphql';

export interface RssSettingsValue {
  maxItemsPerEpisode: RssMaxItemsPerEpisode;
  maxAge: RssMaxAge;
  processingDelay: RssProcessingDelay;
}

interface RssSettingsEditorProps {
  value: RssSettingsValue | undefined;
  onChange: (settings: RssSettingsValue) => void;
}

const MAX_ITEMS_OPTIONS: { id: RssMaxItemsPerEpisode; label: string; summaryText: string }[] = [
  { id: 'ONE', label: '1 item', summaryText: 'a single item' },
  { id: 'THREE', label: '3 items', summaryText: 'up to 3 items' },
  { id: 'FIVE', label: '5 items', summaryText: 'up to 5 items' },
  { id: 'TEN', label: '10 items', summaryText: 'up to 10 items' },
];

const MAX_AGE_OPTIONS: { id: RssMaxAge; label: string; summaryText: string }[] = [
  { id: 'ONE_DAY', label: '24 hours', summaryText: 'the past day' },
  { id: 'ONE_WEEK', label: '7 days', summaryText: 'the past week' },
  { id: 'ONE_MONTH', label: '30 days', summaryText: 'the past month' },
];

const PROCESSING_DELAY_OPTIONS: { id: RssProcessingDelay; label: string; summaryText: string }[] = [
  { id: 'INSTANT', label: 'Immediately', summaryText: 'right away' },
  { id: 'ONE_HOUR', label: 'After 1 hour', summaryText: 'after waiting an hour' },
  { id: 'SIX_HOURS', label: 'After 6 hours', summaryText: 'after waiting 6 hours' },
  { id: 'ONE_DAY', label: 'After 1 day', summaryText: 'after waiting a day' },
  { id: 'TWO_DAYS', label: 'After 2 days', summaryText: 'after waiting 2 days' },
];

export const DEFAULT_RSS_SETTINGS: RssSettingsValue = {
  maxItemsPerEpisode: 'ONE',
  maxAge: 'ONE_DAY',
  processingDelay: 'INSTANT',
};

function getSummaryText(settings: RssSettingsValue): string {
  const items = MAX_ITEMS_OPTIONS.find(o => o.id === settings.maxItemsPerEpisode)?.summaryText ?? 'items';
  const age = MAX_AGE_OPTIONS.find(o => o.id === settings.maxAge)?.summaryText ?? 'recently';
  const delay = PROCESSING_DELAY_OPTIONS.find(o => o.id === settings.processingDelay)?.summaryText ?? '';

  // Build a natural sentence
  const delayPart = settings.processingDelay === 'INSTANT'
    ? 'As soon as new content appears'
    : `When new content appears, ${delay}`;

  return `${delayPart}, an episode will be created with ${items}. Only content from ${age} will be included.`;
}

export function RssSettingsEditor({ value, onChange }: RssSettingsEditorProps) {
  const settings = value ?? DEFAULT_RSS_SETTINGS;

  const handleChange = useCallback((field: keyof RssSettingsValue, newValue: string) => {
    onChange({
      ...settings,
      [field]: newValue,
    });
  }, [settings, onChange]);

  return (
    <div className="space-y-5">
      {/* Human-readable summary */}
      <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 p-4">
        <p className="text-sm text-violet-200 leading-relaxed">
          {getSummaryText(settings)}
        </p>
      </div>

      {/* Settings controls */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="maxItemsPerEpisode" className="label text-xs">Items per episode</label>
          <select
            id="maxItemsPerEpisode"
            value={settings.maxItemsPerEpisode}
            onChange={(e) => handleChange('maxItemsPerEpisode', e.target.value)}
            className="input text-sm"
          >
            {MAX_ITEMS_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="processingDelay" className="label text-xs">Processing delay</label>
          <select
            id="processingDelay"
            value={settings.processingDelay}
            onChange={(e) => handleChange('processingDelay', e.target.value)}
            className="input text-sm"
          >
            {PROCESSING_DELAY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="maxAge" className="label text-xs">Content age limit</label>
          <select
            id="maxAge"
            value={settings.maxAge}
            onChange={(e) => handleChange('maxAge', e.target.value)}
            className="input text-sm"
          >
            {MAX_AGE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
